import React from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, LogOut, Package, Home, Search, Apple } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useShopCart } from '../../context/ShopCartContext';
import { useShopAuth } from '../../context/ShopAuthContext';
import { NotificationBell } from '../../components/NotificationBell';

const LOGO = '/bison-logo.png';

export const ShopLayout = () => {
  const { count } = useShopCart();
  const { customer, token, logout } = useShopAuth();
  const navigate = useNavigate();
  const customerHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <Link to="/shop" data-testid="shop-logo" className="flex items-center gap-2 transition-transform hover:scale-[1.02]">
            <img src={LOGO} alt="Bison" className="h-9 w-auto" />
            <span className="hidden sm:inline font-heading text-xl tracking-tight font-semibold text-primary">Bison Shop</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 ml-6">
            <Link to="/shop" className="px-3 py-2 text-sm hover:text-primary rounded-md" data-testid="shop-nav-home">Home</Link>
            <Link to="/shop/products" className="px-3 py-2 text-sm hover:text-primary rounded-md" data-testid="shop-nav-products">All Products</Link>
            {customer && <Link to="/shop/account" className="px-3 py-2 text-sm hover:text-primary rounded-md" data-testid="shop-nav-account">My Orders</Link>}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/shop/cart" className="relative" data-testid="shop-cart-link">
              <Button variant="outline" size="sm" className="gap-1">
                <ShoppingCart className="w-4 h-4" />
                Cart
                {count > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs" data-testid="cart-badge">{count}</span>
                )}
              </Button>
            </Link>
            {customer ? (
              <div className="flex items-center gap-2">
                <NotificationBell audience="customer" headers={customerHeaders} />
                <span className="hidden sm:inline text-sm text-muted-foreground">Hi, {customer.name.split(' ')[0]}</span>
                <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/shop'); }} data-testid="shop-logout-btn">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Link to="/shop/login" data-testid="shop-login-link">
                <Button variant="default" size="sm" className="gap-1">
                  <User className="w-4 h-4" />
                  Sign in
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/30 mt-12">
        <div className="max-w-7xl mx-auto px-4 py-10 grid gap-8 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src={LOGO} alt="Bison" className="h-8" />
              <span className="font-serif font-semibold">Bison Shop</span>
            </div>
            <p className="text-sm text-muted-foreground">Fresh groceries delivered across Kigali. Order online, pay on delivery or via Mobile Money.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-2 text-sm uppercase tracking-wider text-muted-foreground">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/shop" className="hover:text-primary">Home</Link></li>
              <li><Link to="/shop/products" className="hover:text-primary">Browse All</Link></li>
              <li><Link to="/shop/account" className="hover:text-primary">My Account</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2 text-sm uppercase tracking-wider text-muted-foreground">Get the App</h4>
            <div className="flex flex-col gap-2 mb-4">
              <a href="https://play.google.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-black text-white hover:bg-black/90 text-sm w-fit" data-testid="footer-playstore">
                <Package className="w-5 h-5" /> Google Play
              </a>
              <a href="https://apps.apple.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-black text-white hover:bg-black/90 text-sm w-fit" data-testid="footer-appstore">
                <Apple className="w-5 h-5" /> App Store
              </a>
            </div>
            <Link to="/shop-admin/login" className="text-xs text-muted-foreground hover:text-primary underline" data-testid="footer-staff-link">Staff back-office access →</Link>
          </div>
        </div>
        <div className="border-t border-border py-4 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} Bison Food Solutions Ltd. All rights reserved.
        </div>
      </footer>
    </div>
  );
};
